import {
	setStyle,
	getCurrentTable,
	getElementsBoundary,
	getTdBoundaryIndex,
	getBoundaryRectAndElement,
	getCorrectTableIndexBoundary,
} from './util';
import { AdvancedTableBlock } from '@bay-email/core';
import { 
	mergeCellsOp, 
	deleteColumnsOp, 
	deleteRowsOp, 
	insertColumnRightOp, 
	insertColumnLeftOp, 
	insertRowUpOp, 
	insertRowDownOp 
} from './tableOperations';

interface IBorderTool {
	top: Element;
	bottom: Element;
	left: Element;
	right: Element;
	overlay: Element;
	dot: Element;
}

const SELECTION_BLUE = 'rgb(14, 101, 235)';

class TableColumnTool {
	borderTool = {} as IBorderTool;
	dragging = false;
	dotDragging = false;
	suppressNextClick = false;
	showBorderTool = false;
	startRect = {} as { width: number; height: number }; // start td rect
	startTdTop = 0; // update when click
	startTdLeft = 0; //  update when click
	endTdTop = 0; // will update by mouse move
	endTdLeft = 0; // will update by mouse move
	width = 0; // selected section width,will update by mouse move
	height = 0; // selected section height, will update by mouse move

	selectedLeftTopCell: Element | undefined = undefined;
	selectedBottomRightCell: Element | undefined = undefined;
	startDom: Element | undefined = undefined;
	endDom: Element | undefined = undefined;
	hoveringTable: ParentNode | null = null;
	root: Element | undefined = undefined;

	changeTableData?: (e: AdvancedTableBlock['data']['value']['tableSource']) => void;
	onSelectionChange?: (hasSelection: boolean, canMerge: boolean) => void;
	tableData: AdvancedTableBlock['data']['value']['tableSource'] = [];
	layoutObserver: MutationObserver | null = null;
	private readonly handleRootMousedown = this.handleMousedown.bind(this);

	constructor(borderTool: IBorderTool, root: Element) {
		if (!borderTool || !root) {
			return;
		}
		this.borderTool = borderTool;
		this.root = root;

		this.initTool();
	}

	initTool() {
		this.root?.addEventListener('mousedown', this.handleRootMousedown);
		this.borderTool.dot.addEventListener('mousedown', this.handleDotMousedown as EventListener);
		document.body.addEventListener('click', this.hideBorder, false);
		document.addEventListener('keydown', this.hideBorderByKeyDown);
		document.addEventListener('scroll', this.handleScrollHide, true);
		// The canvas scrolls on .shadow-container inside the shadow root.
		// Scroll does not bubble out of the shadow boundary, so we must
		// also listen on the ShadowRoot itself (capture=true to pierce inward).
		const shadowRoot = this.root?.getRootNode();
		if (shadowRoot && shadowRoot !== document) {
			(shadowRoot as EventTarget).addEventListener('scroll', this.handleScrollHide, true);
		}
		// Re-position the overlay when blocks are inserted/removed anywhere
		// in the editor (e.g. dropping a new block before the table pushes it down).
		if (this.root) {
			this.layoutObserver = new MutationObserver(this.handleLayoutMutation);
			this.layoutObserver.observe(this.root, { childList: true, subtree: true });
		}
	}

	destroy() {
		this.root?.removeEventListener('mousedown', this.handleRootMousedown);
		this.borderTool.dot.removeEventListener('mousedown', this.handleDotMousedown as EventListener);
		document.body.removeEventListener('click', this.hideBorder, false);
		document.removeEventListener('keydown', this.hideBorderByKeyDown);
		document.removeEventListener('scroll', this.handleScrollHide, true);
		// Mirror the shadow-root scroll listener added in initTool — must be
		// removed from the same EventTarget (the ShadowRoot) with the same
		// capture flag so the browser actually deregisters it.
		const shadowRoot = this.root?.getRootNode();
		if (shadowRoot && shadowRoot !== document) {
			(shadowRoot as EventTarget).removeEventListener('scroll', this.handleScrollHide, true);
		}
		this.layoutObserver?.disconnect();
		this.layoutObserver = null;
	}

	hideBorder = (e: any) => {
		// Suppress the click that fires immediately after a dot drag ends
		if (this.suppressNextClick) {
			this.suppressNextClick = false;
			return;
		}
		const actualTarget = (e.composedPath?.() ?? [])[0] ?? e.target;
		if (this.hoveringTable?.contains(actualTarget as Node)) {
			return;
		}
		// Don't hide when the user clicks the resize dot
		if (actualTarget === this.borderTool.dot) {
			return;
		}
		this.visibleBorder(false);
	};

	hideBorderByKeyDown = () => {
		this.visibleBorder(false);
	};

	handleScrollHide = () => {
		if (this.showBorderTool) {
			this.visibleBorder(false);
		}
	};

	handleLayoutMutation = (mutations: MutationRecord[]) => {
		if (!this.showBorderTool) return;
		for (const mutation of mutations) {
			if (mutation.addedNodes.length === 0 && mutation.removedNodes.length === 0) continue;
			// Ignore mutations inside the selected table (typing, our own DOM changes).
			if (this.hoveringTable?.contains(mutation.target as Node)) continue;
			// A structural change outside the table means layout shifted —
			// re-read getBoundingClientRect and reposition the overlay.
			requestAnimationFrame(() => {
				if (this.showBorderTool) this.renderBorder();
			});
			return;
		}
	};

	visibleBorder = (show = true) => {
		if (this.showBorderTool === show) {
			return;
		}
		if (show) {
			setStyle(this.borderTool.top.parentElement, { display: 'block' });
		} else {
			setStyle(this.borderTool.top.parentElement, { display: 'none' });
			this.onSelectionChange?.(false, false);
		}
		this.showBorderTool = show;
	};

	renderBorder = () => {
		const result = getBoundaryRectAndElement(
			this.startDom as Element,
			this.endDom as Element,
		);
		if (!result) {
			return;
		}
		this.selectedLeftTopCell = result.leftTopCell;
		this.selectedBottomRightCell = result.bottomRightCell;

		const viewportRect = getElementsBoundary(
			this.selectedLeftTopCell,
			this.selectedBottomRightCell,
		);
		const left = viewportRect.left;
		const top = viewportRect.top;
		const width = viewportRect.right - viewportRect.left;
		const height = viewportRect.bottom - viewportRect.top;

		setStyle(this.borderTool.overlay, {
			'background-color':SELECTION_BLUE ,
			opacity: '0.1',
			left: `${left}px`,
			top: `${top}px`,
			width: `${Math.abs(width)}px`,
			height: `${Math.abs(height)}px`,
			position: 'fixed',
			'z-index': 9,
			'pointer-events': 'none',
		});

		setStyle(this.borderTool.top, {
			'background-color': SELECTION_BLUE,
			left: `${left}px`,
			top: `${top}px`,
			width: `${Math.abs(width)}px`,
			height: '1.5px',
			position: 'fixed',
			'z-index': 10,
		});
		setStyle(this.borderTool.bottom, {
			'background-color': SELECTION_BLUE,
			left: `${left}px`,
			top: `${top + height}px`,
			width: `${Math.abs(width)}px`,
			height: '1.5px',
			position: 'fixed',
			'z-index': 10,
		});
		setStyle(this.borderTool.left, {
			'background-color': SELECTION_BLUE,
			left: `${left}px`,
			top: `${top}px`,
			width: `1.5px`,
			height: `${Math.abs(height)}px`,
			position: 'fixed',
			'z-index': 10,
		});
		setStyle(this.borderTool.right, {
			'background-color': SELECTION_BLUE,
			left: `${left + width}px`,
			top: `${top}px`,
			width: `1.5px`,
			height: `${Math.abs(height)}px`,
			position: 'fixed',
			'z-index': 10,
		});

		const DOT_SIZE = 8;
		setStyle(this.borderTool.dot, {
			'background-color': SELECTION_BLUE,
			'border-radius': '50%',
			left: `${left + width - DOT_SIZE / 2}px`,
			top: `${top + height - DOT_SIZE / 2}px`,
			width: `${DOT_SIZE}px`,
			height: `${DOT_SIZE}px`,
			position: 'fixed',
			'z-index': 11,
			// 'pointer-events': 'none',
			cursor: 'nwse-resize',
		});

		this.visibleBorder(true);
		const canMerge = this.selectedLeftTopCell !== this.selectedBottomRightCell;
		this.onSelectionChange?.(true, canMerge);
	};

	handleDotMousedown = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		this.dotDragging = true;
		document.addEventListener('mousemove', this.handleDotDrag);
		document.addEventListener('mouseup', this.handleDotMouseup);
	};

	handleDotDrag = (e: MouseEvent) => {
		if (!this.dotDragging) return;
		e.preventDefault();

		// The table lives inside a shadow root. Use the shadow root's
		// elementFromPoint so we can pierce the shadow boundary.
		const shadowRoot = this.root?.getRootNode() as ShadowRoot;
		const el: Element | null = shadowRoot?.elementFromPoint
			? shadowRoot.elementFromPoint(e.clientX, e.clientY)
			: document.elementFromPoint(e.clientX, e.clientY);

		if (!el) return;

		let target: Element | null = el;
		while (target) {
			if (
				target.nodeName === 'TD' &&
				target.getAttribute('data-content_editable-type') === 'rich_text'
			) {
				const hoveringTable = getCurrentTable(target);
				if (this.hoveringTable !== hoveringTable) return;
				if (this.endDom === target) return;
				this.endDom = target;
				this.renderBorder();
				return;
			}
			target = target.parentElement;
		}
	};

	handleDotMouseup = (e: MouseEvent) => {
		e.preventDefault();
		this.dotDragging = false;
		// The browser fires a click event on document.body right after mouseup.
		// That click would reach hideBorder and clear the selection, so suppress it.
		this.suppressNextClick = true;
		document.removeEventListener('mousemove', this.handleDotDrag);
		document.removeEventListener('mouseup', this.handleDotMouseup);
	};

	handleMousedown(event: any) {
		let target: Element = event.target;
		if (event.button == 0) {
			// left button click
			while (target && target.parentNode) {
				if (
					target.nodeName === 'TD' &&
					target.getAttribute('data-content_editable-type') === 'rich_text'
				) {
					this.root?.addEventListener('mousemove', this.handleDrag);
					this.root?.addEventListener('mouseup', this.handleMouseup);

					this.dragging = true;
					this.startDom = target;
					this.endDom = target;
					this.hoveringTable = getCurrentTable(target);

					this.renderBorder();
					return;
				}
				target = target.parentNode as Element;
				if (['TR', 'TABLE', 'BODY'].includes(target.nodeName)) {
					this.visibleBorder(false);
					return;
				}
			}
		}
		this.visibleBorder(false);
	}

	handleDrag = (e: any) => {
		e.preventDefault();

		if (this.dragging) {
			let target = e.target;

			while (target && target.parentNode) {
				if (
					target.nodeName === 'TD' &&
					target.getAttribute('data-content_editable-type') === 'rich_text'
				) {
					const hoveringTable = getCurrentTable(target);
					if (this.endDom === target || this.hoveringTable !== hoveringTable) {
						return;
					}
					this.endDom = target;
					this.renderBorder();
					return;
				}
				target = target.parentNode;
			}
		}
	};

	handleMouseup = (e: any) => {
		e.preventDefault();

		if (this.dragging) {
			this.dragging = false;
			this.root?.removeEventListener('mousemove', this.handleDrag);
			this.root?.removeEventListener('mouseup', this.handleMouseup);
		}
	};

	private getSelectionBoundary() {
		if (!this.selectedLeftTopCell || !this.selectedBottomRightCell) return null;
		const rawBoundary = getTdBoundaryIndex(
			this.selectedLeftTopCell,
			this.selectedBottomRightCell,
		);
		return getCorrectTableIndexBoundary(rawBoundary, this.tableData as any);
	}

	mergeCells() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		mergeCellsOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}

	deleteSelectedColumns() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		deleteColumnsOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}

	deleteSelectedRows() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		deleteRowsOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}

	insertRowUp() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		insertRowUpOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}

	insertRowDown() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		insertRowDownOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}

	insertColumnLeft() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		insertColumnLeftOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}

	insertColumnRight() {
		const boundary = this.getSelectionBoundary();
		if (!boundary) return;
		insertColumnRightOp(this.tableData as any, boundary, data => this.changeTableData?.(data));
		this.visibleBorder(false);
	}
}

export default TableColumnTool;
